<template>
  <material-card>
    <v-layout slot="header" px-3>
      <v-flex my-4 pl-4 title align-center justify-center>Użycie usług</v-flex>

      <v-spacer />
    </v-layout>

    <v-layout v-if="!!count">
      <v-flex text-xs-right>
        <check-permission permissions="BENEFIT_USE.CSV">
          <div>
            <post-form text="Pobierz CSV" :url="csvUrl" :fields="csvFilters" />

            <v-btn v-if="global" class="v-btn--large" @click="showFilters = !showFilters">Filtry</v-btn>
          </div>
        </check-permission>
      </v-flex>
    </v-layout>

    <div v-if="global">
      <v-expansion-panel>
        <v-expansion-panel-content v-model="showFilters">
          <v-card class="elevation-5">
            <v-layout px-3 pt-2 elevation-1>
              <v-flex xs3>
                <v-autocomplete
                  v-model="selectedPartner"
                  clearable
                  hide-no-data
                  placeholder="Wyszukaj partnera"
                  item-text="name"
                  item-value="id"
                  :items="partnerList"
                  :search-input.sync="partnerSearch"
                >
                  <div slot="label">Partner</div>
                </v-autocomplete>
              </v-flex>

              <v-flex xs3>
                <v-autocomplete
                  v-model="selectedBenefit"
                  clearable
                  hide-no-data
                  placeholder="Wyszukaj usługę"
                  item-text="name"
                  item-value="id"
                  :items="benefitList"
                  :search-input.sync="benefitSearch"
                >
                  <div slot="label">Usługa</div>
                </v-autocomplete>
              </v-flex>

              <v-flex xs3>
                <date-picker title="Początek okresu" :value="dateStart" @input="dateStart = $event" />
              </v-flex>

              <v-flex xs3>
                <date-picker title="Koniec okresu" :value="dateEnd" @input="dateEnd = $event" />
              </v-flex>
            </v-layout>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </div>

    <material-table :headers="headers" :items="list" :total-count="count" @details="openProfile" />
  </material-card>
</template>

<script>
import { DisplayBenefitType, DisplayBenefitTypeTranslation } from '@witkac/kartamiejska-shared';
import { numberFormatMoney } from '@witkac/utils/numberFormatMoney';
import moment from 'moment';
import { mapState } from 'vuex';

import DatePicker from '@/components/forms/DatePickerNoSchema';
import MaterialCard from '@/components/material/Card';
import MaterialTable from '@/components/material/Table';
import PostForm from '@/components/postForm/PostForm';
import { listMixin } from '@/mixins/list';
import { baseFilters } from '@/utils/baseFilters';
import { config } from '@/utils/config';
import { tryParseInt } from '@witkac/utils/tryParseInt';

// @vuese
// Wyświetlanie zakładki historii usług klienta
// @group Client-Profile
export default {
  components: { MaterialCard, MaterialTable, PostForm, DatePicker },
  mixins: [listMixin('benefitsHistory', { listError: 'Wystąpił błąd podczas pobierania historii.' })],
  props: {
    // Czy pobierac historie partnera
    getPartner: {
      type: Boolean,
      required: false,
      default: false
    },
    // Czy pokazać globalną historię użyć
    global: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      showFilters: false,
      csvUrl: config.csvURL + '/benefitUse',
      partnerSearch: '',
      benefitSearch: '',
      selectedPartner: this.$route.query.partnerId,
      selectedBenefit: this.$route.query.benefitId,
      dateStart: this.$route.query.dateStart,
      dateEnd: this.$route.query.dateEnd
    };
  },
  computed: {
    ...mapState({
      canLink: state => state.access.checked['PROFILE.GET'],
      list: state =>
        state.benefitsHistory.list.map(benefitUse => {
          benefitUse.placeName = benefitUse.place.name;
          benefitUse.created = moment(benefitUse.created).format('YYYY-MM-DD HH:mm');
          benefitUse.name = benefitUse.benefit.name;
          benefitUse.type = `${DisplayBenefitTypeTranslation[benefitUse.benefit.type]}${
            benefitUse.benefit.type === DisplayBenefitType.PERCENT_DISCOUNT ? ` (${benefitUse.benefit.value}%)` : ''
          }`;

          switch (benefitUse.benefit.type) {
            case DisplayBenefitType.AMOUNT_DISCOUNT:
              benefitUse.benefitValue = benefitUse.benefit.value;
              break;
            case DisplayBenefitType.TIME:
              benefitUse.benefitValue = benefitUse.value + ' min';
              break;
            case DisplayBenefitType.PERCENT_DISCOUNT:
              benefitUse.benefitValue = benefitUse.price * (benefitUse.benefit.value / 100);
              break;
            case DisplayBenefitType.FIXED_COST:
              benefitUse.benefitValue = benefitUse.price - benefitUse.benefit.value;
              break;
            default:
              benefitUse.benefitValue = benefitUse.value;
              break;
          }
          benefitUse.paid = benefitUse.price - benefitUse.benefitValue;
          return benefitUse;
        }),
      partnerList: state => state.partners.list,
      benefitList: state => state.benefit.list,
      partner: state => state.partners.data,
      benefit: state => state.benefit.data,
      profileId: state => state.client?.data?.profile?.id,
      clientId: state => state.client?.id
    }),
    headers() {
      return [
        { value: 'created', text: 'Data', sortable: true },
        {
          value: 'cardNumber',
          text: 'Numer karty',
          link: this.canLink,
          sortable: false,
          render: item => (item.card ? item.card.number : 'Brak')
        },
        { value: 'placeName', text: 'Obiekt', sortable: false },
        { value: 'name', text: 'Usługa', sortable: false },
        { value: 'type', text: 'Rodzaj zniżki', sortable: false },
        {
          value: 'price',
          text: 'Cena bazowa',
          sortable: true,
          render: item => numberFormatMoney(item.price) + ' zł'
        },
        {
          value: 'benefitValue',
          text: 'Udzielona zniżka',
          sortable: false,
          render: item => numberFormatMoney(item.benefitValue) + ' zł'
        },
        { value: 'paid', text: 'Zapłacono', sortable: false, render: item => numberFormatMoney(item.paid) + ' zł' }
      ];
    },
    csvFilters() {
      const filters = this.filters(baseFilters(this.$route), this.$route);
      if (!global && !this.getPartner) {
        filters.profileId === this.$store.state.client.data.profile.id;
      }
      if (this.$route.query.partnerId) {
        filters.partnerId = +this.$route.query.partnerId;
      }
      if (this.$route.query.benefitId) {
        filters.benefitId = +this.$route.query.benefitId;
      }
      if (this.$route.query.dateStart) {
        filters.dateStart = this.$route.query.dateStart;
      }
      if (this.$route.query.dateEnd) {
        filters.dateEnd = this.$route.query.dateEnd;
      }
      return Object.keys(filters)
        .filter(key => !['count', 'start'].includes(key))
        .map(key => ({
          name: key,
          value: typeof filters[key] === 'object' ? JSON.stringify(filters[key]) : filters[key]
        }));
    }
  },
  watch: {
    partnerSearch: {
      immediate: true,
      handler(value) {
        if (this.global && value) {
          this.searchPartner(value);
          this.selectedBenefit = undefined;
          this.benefitSearch = '';
          this.searchBenefit(undefined, value);
        }
      }
    },
    benefitSearch: {
      immediate: true,
      handler(value) {
        if (this.global && value) {
          this.searchBenefit(value);
        }
      }
    },
    selectedPartner(partnerId) {
      if (this.global) {
        this.$router.replace({ ...this.$route, query: { ...this.$route.query, partnerId } });
      }
    },
    selectedBenefit(benefitId) {
      if (this.global) {
        this.$router.replace({ ...this.$route, query: { ...this.$route.query, benefitId } });
      }
    },
    dateStart(date) {
      if (this.global) {
        this.$router.replace({ ...this.$route, query: { ...this.$route.query, dateStart: date || undefined } });
      }
    },
    dateEnd(date) {
      if (this.global) {
        this.$router.replace({ ...this.$route, query: { ...this.$route.query, dateEnd: date || undefined } });
      }
    }
  },
  mounted() {
    if (!this.global) {
      return;
    }
    if (this.$route.query.partnerId) {
      this.partnerList.push(this.partner);
      this.selectedPartner = +this.$route.query.partnerId;
    }
    if (this.$route.query.benefitId) {
      this.benefitList.push(this.benefit);
      this.selectedBenefit = +this.$route.query.benefitId;
    }
    if (this.$route.query.dateStart) {
      this.dateStart = this.$route.query.dateStart;
    }
    if (this.$route.query.dateEnd) {
      this.dateEnd = this.$route.query.dateEnd;
    }
  },
  methods: {
    /**
     * Tworzy filtry na podstawie paginacji
     *
     * @param {*} pagination
     * @returns
     */
    filters(pagination) {
      const filters = { ...pagination };

      if (this.getPartner && this.$store.state?.partners?.id) {
        return { ...filters, partnerId: this.$store.state.partners.id };
      } else if (this.$store.state.client?.data?.profile?.id) {
        return { ...filters, profileId: this.$store.state.client.data.profile.id };
      }
      return filters;
    },
    // @vuese
    // Przekierowanie na profil osoby przypisanej do karty
    openProfile(item) {
      this.$router.push({ name: 'client', params: { id: item.profile.userId } });
    },

    // @vuese
    // Wyszukanie partnerów
    searchPartner(search) {
      if (!this.global) {
        return;
      }
      this.$store.dispatch('partners/list', { search, count: 5 });
    },
    // @vuese
    // Wyszukanie usług
    searchBenefit(search) {
      if (!this.global) {
        return;
      }

      const filters = { count: 5, withInactive: true };
      if (this.selectedPartner) {
        filters.partnerId = tryParseInt(this.selectedPartner);
      }

      if (search) {
        filters.search = search;
      }
      this.$store.dispatch('benefit/list', filters);
    },
    // @vuese
    // Nadpisanie przeładowania listy z mixina
    reloadList() {
      const filters = {
        ...baseFilters(this.$route)
      };
      if (this.clientId && this.clientId === this.$route.params.id && this.profileId) {
        filters.profileId = this.profileId;
      }
      if (this.selectedPartner || this.$route.params.id) {
        filters.partnerId = this.$route.params.id ? tryParseInt(this.$route.params.id) : this.selectedPartner;
      }
      if (this.selectedBenefit) {
        filters.benefitId = this.selectedBenefit;
      }
      if (this.dateStart) {
        filters.dateStart = this.dateStart;
      }
      if (this.dateEnd) {
        filters.dateEnd = this.dateEnd;
      }
      return this.$store
        .dispatch('benefitsHistory/list', filters)
        .catch(err => this.notifyError('Wystąpił błąd podczas pobierania historii.'));
    }
  }
};
</script>
